// src/workspace-dock-layout.ts
var DOCK_PANEL_WIDTH = 360;
var DOCK_MIN_PANEL_WIDTH = 220;
var DOCK_MAX_PANEL_WIDTH = 375;
var DOCK_MIN_PANEL_HEIGHT = 120;
var DOCK_SIDEBAR_COLUMN_WIDTH = DOCK_MAX_PANEL_WIDTH;
var DOCK_FLOATING_PANEL_HEIGHT = 360;
var DOCK_GAP = 10;
var GAP = DOCK_GAP;
var HEADER = 34;
function branch(axis, children, sizes) {
  const kept = children.flatMap((node, index) => node ? [{ node, size: sizes?.[index] ?? 1 }] : []);
  if (kept.length < 2) return kept[0]?.node;
  return sizes ? { axis, children: kept.map((item) => item.node), sizes: kept.map((item) => item.size) } : { axis, children: kept.map((item) => item.node) };
}
function panelIds(tree) {
  return !tree ? [] : "panel" in tree ? [tree.panel] : tree.children.flatMap((node) => panelIds(node));
}
function withoutPanel(tree, id) {
  return !tree ? void 0 : "panel" in tree ? tree.panel === id ? void 0 : tree : branch(tree.axis, tree.children.map((node) => withoutPanel(node, id)), tree.sizes);
}
function insertPanel(tree, id, target, edge) {
  const item = { panel: id };
  if (!tree) return item;
  if (!target) return branch("vertical", [tree, item]);
  if ("panel" in tree) {
    if (tree.panel !== target) return tree;
    return { axis: edge === "left" || edge === "right" ? "horizontal" : "vertical", children: edge === "left" || edge === "top" ? [item, tree] : [tree, item] };
  }
  return { ...tree, children: tree.children.map((node) => insertPanel(node, id, target, edge)) };
}
function completeTree(tree, panels) {
  const ids = new Set(panelIds(tree));
  return branch("vertical", [tree, ...panels.filter((panel) => !ids.has(panel.id)).map((panel) => ({ panel: panel.id }))]);
}
function visibleTree(tree, ids) {
  return !tree ? void 0 : "panel" in tree ? ids.has(tree.panel) ? tree : void 0 : branch(tree.axis, tree.children.map((node) => visibleTree(node, ids)), tree.sizes);
}
function autoTree(panels, maxColumns) {
  const expanded = panels.filter((panel) => !panel.collapsed);
  const collapsed = panels.filter((panel) => panel.collapsed).map((panel) => ({ panel: panel.id }));
  const tall = expanded.filter((panel) => panel.tall);
  const short = expanded.filter((panel) => !panel.tall);
  const tallWidth = tall.reduce((sum, panel) => sum + (panel.columns ?? 1), 0);
  const shortColumns = short.length ? Math.min(short.length, Math.max(1, Math.floor(maxColumns) - tallWidth)) : 0;
  const columns = tall.map((panel) => ({ first: expanded.indexOf(panel), tall: true, nodes: [{ panel: panel.id }] }));
  let start = 0;
  for (let column = 0; column < shortColumns; column++) {
    const size = Math.ceil((short.length - start) / (shortColumns - column));
    const stack = short.slice(start, start + size);
    columns.push({ first: expanded.indexOf(stack[0]), tall: false, nodes: stack.map((panel) => ({ panel: panel.id })) });
    start += size;
  }
  columns.sort((a, b) => a.first - b.first);
  if (collapsed.length) {
    const foot = [...columns].reverse().find((column) => !column.tall) ?? columns[columns.length - 1];
    if (foot) foot.nodes.push(...collapsed);
    else columns.push({ first: 0, tall: false, nodes: collapsed });
  }
  return branch("horizontal", columns.map((column) => branch("vertical", column.nodes)));
}
function columnCount(tree, panels = {}) {
  if (!tree) return 0;
  if ("panel" in tree) return panels[tree.panel]?.columns ?? 1;
  const counts = tree.children.map((child) => columnCount(child, panels));
  return tree.axis === "horizontal" ? counts.reduce((sum, count) => sum + count, 0) : Math.max(0, ...counts);
}
function treeRects(tree, box, panels, result = {}, { fill = false, dividers } = {}) {
  if (!tree) return result;
  const count = columnCount(tree, panels);
  const columns = box.width >= count * DOCK_MIN_PANEL_WIDTH + GAP * (count - 1);
  const fixedHeight = (node) => {
    if ("panel" in node) return panels[node.panel]?.collapsed ? panels[node.panel].headerHeight ?? HEADER : null;
    const heights = node.children.map(fixedHeight);
    if (heights.some((height) => height === null)) return null;
    return columns && node.axis === "horizontal" ? Math.max(...heights) : heights.reduce((sum, value) => sum + value, GAP * (heights.length - 1));
  };
  const card = (height) => fill ? height : Math.min(height, DOCK_FLOATING_PANEL_HEIGHT);
  const stack = (nodes, bounds, owner) => {
    const fixed = nodes.map(fixedHeight);
    const weights = owner?.sizes;
    const flexibleWeight = nodes.reduce((sum, _, index) => fixed[index] === null ? sum + (weights?.[index] ?? 1) : sum, 0);
    const available = Math.max(0, bounds.height - GAP * (nodes.length - 1));
    const fixedTotal = fixed.reduce((sum, value) => sum + (value ?? 0), 0);
    const flexible = fixed.filter((value) => value === null).length;
    const fixedScale = fixedTotal > available ? available / fixedTotal : 1;
    const share = Math.max(0, available - fixedTotal) / (flexible || 1);
    const slack = flexible || !fill ? 0 : Math.max(0, available - fixedTotal * fixedScale);
    const rest = Math.max(0, available - fixedTotal);
    let offset = 0;
    const weightedBudget = fill ? rest : Math.min(rest, flexible * DOCK_FLOATING_PANEL_HEIGHT);
    const lengths = nodes.map((child, index) => {
      const size = fixed[index] === null ? weights ? weightedBudget * (weights[index] ?? 1) / (flexibleWeight || 1) : card(share) : fixed[index] * fixedScale + (index === nodes.length - 1 ? slack : 0);
      visit(child, { ...bounds, y: bounds.y + offset, height: size });
      offset += size + GAP;
      return size;
    });
    if (!owner || !dividers) return;
    let edge = bounds.y;
    nodes.forEach((_, index) => {
      edge += lengths[index];
      if (index < nodes.length - 1 && fixed[index] === null && fixed[index + 1] === null) dividers.push({
        axis: "vertical",
        rect: { x: bounds.x, y: edge, width: bounds.width, height: GAP },
        between: [index, index + 1],
        groups: nodes.map((node) => panelIds(node)),
        pixels: nodes.map((__, k) => fixed[k] === null ? lengths[k] : null),
        spans: nodes.map(() => 1),
        weights: nodes.map((__, k) => weights?.[k] ?? 1)
      });
      edge += GAP;
    });
  };
  const visit = (node, bounds) => {
    if ("panel" in node) {
      const panel = panels[node.panel];
      result[node.panel] = { ...bounds, height: panel?.collapsed && !fill ? Math.min(bounds.height, panel.headerHeight ?? HEADER) : bounds.height };
      return;
    }
    if (node.axis === "vertical") return stack(node.children, bounds, node);
    if (!columns) return stack(node.children, bounds);
    const live = fill ? node.children.filter((child) => fixedHeight(child) === null) : [];
    const lanes = (live.length ? live : node.children).map((child) => ({ child, folded: [] }));
    if (live.length) node.children.forEach((child, index) => {
      if (live.includes(child)) return;
      const before = node.children.slice(0, index).reverse().find((item) => live.includes(item));
      const lane = lanes.find((item) => item.child === (before ?? node.children.slice(index).find((item2) => live.includes(item2))));
      lane.folded.push(child);
    });
    const spans = lanes.map((lane) => columnCount(lane.child, panels));
    const slots = spans.reduce((sum, span) => sum + span, 0);
    const slotWidth = Math.max(0, (bounds.width - GAP * (slots - 1)) / slots);
    const weights = node.sizes;
    const laneWeight = (index) => spans[index] * (weights?.[node.children.indexOf(lanes[index].child)] ?? 1);
    const totalWeight = lanes.reduce((sum, _, index) => sum + laneWeight(index), 0);
    const inner = Math.max(0, bounds.width - GAP * (lanes.length - 1));
    let offset = 0;
    const widths = lanes.map((lane, index) => {
      const width = weights ? inner * laneWeight(index) / (totalWeight || 1) : slotWidth * spans[index] + GAP * (spans[index] - 1);
      const laneBounds = { ...bounds, x: bounds.x + offset, width, height: !fill && "panel" in lane.child ? card(bounds.height) : bounds.height };
      if (lane.folded.length) stack([lane.child, ...lane.folded], laneBounds);
      else visit(lane.child, laneBounds);
      offset += width + GAP;
      return width;
    });
    if (!dividers) return;
    const pixels = node.children.map((child) => {
      const index = lanes.findIndex((lane) => lane.child === child);
      return index < 0 ? null : widths[index];
    });
    let edge = bounds.x;
    lanes.slice(0, -1).forEach((lane, index) => {
      edge += widths[index];
      dividers.push({
        axis: "horizontal",
        rect: { x: edge, y: bounds.y, width: GAP, height: bounds.height },
        between: [node.children.indexOf(lane.child), node.children.indexOf(lanes[index + 1].child)],
        groups: node.children.map((child) => panelIds(child)),
        pixels,
        spans: node.children.map((child) => columnCount(child, panels)),
        weights: node.children.map((_, k) => weights?.[k] ?? 1)
      });
      edge += GAP;
    });
  };
  visit(tree, !fill && "panel" in tree ? { ...box, height: card(box.height) } : box);
  return result;
}
function dividerWeights(divider, delta, min = divider.axis === "horizontal" ? DOCK_MIN_PANEL_WIDTH : DOCK_MIN_PANEL_HEIGHT) {
  const [a, b] = divider.between;
  const pixels = [...divider.pixels];
  const first = pixels[a] ?? 0, second = pixels[b] ?? 0;
  const moved = Math.max(Math.min(0, min - first), Math.min(Math.max(0, second - min), delta));
  pixels[a] = first + moved;
  pixels[b] = second - moved;
  const raw = pixels.map((length, index) => length === null ? null : length / (divider.spans[index] || 1));
  const live = raw.filter((value) => value !== null);
  const mean = live.reduce((sum, value) => sum + value, 0) / (live.length || 1) || 1;
  return raw.map((value, index) => value === null ? divider.weights[index] : value / mean);
}
function resizeTree(tree, axis, visible, groups, weights) {
  if (!tree || "panel" in tree) return tree;
  const kept = tree.children.map((child, index) => ({ index, ids: panelIds(visibleTree(child, visible)) })).filter((child) => child.ids.length);
  if (tree.axis === axis && kept.length === groups.length && kept.every((child, i) => child.ids.join("\n") === groups[i].join("\n"))) {
    const sizes = tree.children.map((_, index) => tree.sizes?.[index] ?? 1);
    kept.forEach((child, i) => {
      sizes[child.index] = weights[i] ?? 1;
    });
    return { ...tree, sizes };
  }
  return { ...tree, children: tree.children.map((child) => resizeTree(child, axis, visible, groups, weights) ?? child) };
}
function readSplits(key) {
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? "{}");
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    const parse = (node, seen, depth = 0) => {
      if (!node || typeof node !== "object" || depth > 20) return;
      if ("panel" in node && typeof node.panel === "string" && node.panel.length && !seen.has(node.panel)) {
        seen.add(node.panel);
        return { panel: node.panel };
      }
      if ("axis" in node && (node.axis === "horizontal" || node.axis === "vertical") && "children" in node && Array.isArray(node.children)) {
        const children = node.children.slice(0, 100);
        const sizes = "sizes" in node && Array.isArray(node.sizes) && node.sizes.length === node.children.length && node.sizes.every((size) => typeof size === "number" && Number.isFinite(size) && size > 0) ? node.sizes.slice(0, 100) : void 0;
        return branch(node.axis, children.map((child) => parse(child, seen, depth + 1)), sizes);
      }
    };
    const result = {};
    for (const key2 of ["left", "right", "sidebar"]) if (key2 in value) {
      const node = parse(value[key2], /* @__PURE__ */ new Set());
      if (node) result[key2] = node;
    }
    return result;
  } catch {
    return {};
  }
}
function minimumDockHeight(tree, width, panels) {
  if (!tree) return 0;
  const count = columnCount(tree, panels);
  const columns = width >= count * DOCK_MIN_PANEL_WIDTH + DOCK_GAP * Math.max(0, count - 1);
  const measure = (node) => {
    if ("panel" in node) return panels[node.panel]?.collapsed ? panels[node.panel].headerHeight ?? HEADER : DOCK_MIN_PANEL_HEIGHT;
    const heights = node.children.map(measure);
    return columns && node.axis === "horizontal" ? Math.max(0, ...heights) : heights.reduce((sum, value) => sum + value, DOCK_GAP * Math.max(0, heights.length - 1));
  };
  return measure(tree);
}

export {
  DOCK_PANEL_WIDTH,
  DOCK_MIN_PANEL_WIDTH,
  DOCK_MAX_PANEL_WIDTH,
  DOCK_MIN_PANEL_HEIGHT,
  DOCK_SIDEBAR_COLUMN_WIDTH,
  DOCK_FLOATING_PANEL_HEIGHT,
  DOCK_GAP,
  panelIds,
  withoutPanel,
  insertPanel,
  completeTree,
  visibleTree,
  autoTree,
  columnCount,
  treeRects,
  dividerWeights,
  resizeTree,
  readSplits,
  minimumDockHeight
};
//# sourceMappingURL=chunk-RBO2C7NT.js.map